import { useMemo } from 'react';
import {
  makeStyles,
  tokens,
  Body1,
  Body2,
  Caption1,
  Title3,
  Button,
  Spinner,
} from '@fluentui/react-components';
import {
  People24Regular,
  Briefcase24Regular,
  Money24Regular,
  Receipt24Regular,
  Add24Regular,
  ChevronRight24Regular,
  Warning24Regular,
  Phone24Regular,
} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import { useSharePointList } from '@/hooks/useSharePointList';
import type {
  Employee,
  Project,
  Application,
  Expense,
  Allocation,
  AppAssignment,
  CloudSpend,
  CostAllocation,
  DeviceEnrollment,
} from '@/models/types';
import { useAppContext } from '@/store/appContext';
import { formatCurrency } from '@/utils/formatters';
import { canViewCosts } from '@/utils/permissions';

const COLORS = ['#5B92E5', '#7DB9B3', '#D8A878', '#A855F7', '#22C55E', '#F59E0B', '#06B6D4'];

interface PendingAction {
  key: string;
  severity: 'high' | 'medium';
  icon: JSX.Element;
  label: string;
  detail: string;
  route: string;
}

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: '20px' },
  // Large iOS-style title (hidden on mobile since topbar shows it)
  title: {
    fontSize: '32px',
    fontWeight: 700,
    letterSpacing: '-0.5px',
    '@media (max-width: 768px)': { display: 'none' },
  },
  greeting: {
    color: tokens.colorNeutralForeground3,
    marginBottom: '4px',
    '@media (max-width: 768px)': {
      fontSize: '14px',
      marginBottom: '8px',
    },
  },
  // Primary CTA — single prominent button
  ctaButton: {
    width: '100%',
    justifyContent: 'center',
    padding: '14px',
    fontSize: '16px',
    fontWeight: 600,
    '@media (min-width: 769px)': {
      maxWidth: '260px',
    },
  },
  // Hero metric cards — 2 on mobile, 4 on desktop
  heroGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '10px',
    '@media (min-width: 769px)': {
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '16px',
    },
  },
  heroCard: {
    padding: '16px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow2,
    cursor: 'pointer',
    transition: 'transform 0.1s ease',
    '&:active': { transform: 'scale(0.98)' },
  },
  heroLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  heroValue: {
    fontSize: '34px',
    fontWeight: 700,
    letterSpacing: '-0.8px',
    marginTop: '4px',
    fontFamily: '"SF Mono", ui-monospace, "JetBrains Mono", Menlo, monospace',
    fontVariantNumeric: 'tabular-nums',
    color: tokens.colorNeutralForeground1,
    lineHeight: 1,
    '@media (max-width: 768px)': { fontSize: '26px' },
  },
  // Section header + list card
  section: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 700,
    letterSpacing: '-0.3px',
    padding: '0 4px',
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow2,
    overflow: 'hidden',
  },
  cardPadded: {
    padding: '16px',
  },
  listRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: `0.5px solid ${tokens.colorNeutralStroke3}`,
    gap: '12px',
    cursor: 'pointer',
    '&:last-child': { borderBottom: 'none' },
    '&:active': { backgroundColor: tokens.colorNeutralBackground2 },
  },
  listIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
  },
  listBody: { flex: 1, minWidth: 0 },
  chartContainer: {
    height: '180px',
    '@media (min-width: 769px)': { height: '220px' },
  },
  // Pending admin actions card
  alertCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow2,
    overflow: 'hidden' as const,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  alertHeader: {
    padding: '12px 16px',
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorPaletteYellowForeground2,
    fontWeight: 600,
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    borderBottom: `1px solid ${tokens.colorPaletteYellowBorderActive}`,
  },
  alertRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    gap: '12px',
    cursor: 'pointer',
    borderBottom: `0.5px solid ${tokens.colorNeutralStroke3}`,
    '&:last-child': { borderBottom: 'none' },
    '&:hover': { backgroundColor: tokens.colorNeutralBackground2 },
    '&:active': { backgroundColor: tokens.colorNeutralBackground3 },
  },
  alertIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    flexShrink: 0,
  },
  alertIconHigh: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground1,
  },
  alertIconMed: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorPaletteYellowForeground2,
  },
});

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

export function Dashboard() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { currentUser, listIds } = useAppContext();

  const { items: employees, loading: empLoading } = useSharePointList<Employee>('OC_Employees', { autoFetch: !!listIds });
  const { items: projects } = useSharePointList<Project>('OC_Projects', { autoFetch: !!listIds });
  const { items: allocations } = useSharePointList<Allocation>('OC_Allocations', { autoFetch: !!listIds });
  const { items: apps } = useSharePointList<Application>('OC_Applications', { autoFetch: !!listIds });
  const { items: assignments } = useSharePointList<AppAssignment>('OC_AppAssignments', { autoFetch: !!listIds });
  const { items: expenses } = useSharePointList<Expense>('OC_Expenses', { autoFetch: !!listIds });
  const { items: cloudSpend } = useSharePointList<CloudSpend>('OC_CloudSpend', { autoFetch: !!listIds });
  // Optional admin-only lists — only fetch if list exists and user is admin
  const isAdmin = currentUser?.role === 'Admin';
  const { items: costAllocations } = useSharePointList<CostAllocation>(
    'OC_CostAllocations',
    { autoFetch: !!listIds?.OC_CostAllocations && isAdmin },
  );
  const { items: devices } = useSharePointList<DeviceEnrollment>('OC_Devices', {
    autoFetch: !!listIds?.OC_Devices && isAdmin,
  });

  const stats = useMemo(() => {
    const activeEmployees = employees.filter((e) => e.fields.Status === 'Active').length;
    const activeProjects = projects.filter((p) => p.fields.Status === 'Active').length;
    const pendingExpenses = expenses.filter((e) => e.fields.Status === 'Submitted').length;

    // Total monthly = cloud spend + license cost
    const appCostById = new Map<number, number>();
    for (const a of apps) appCostById.set(Number(a.id), a.fields.MonthlyCostPerLicense ?? 0);
    const licenseCost = assignments
      .filter((x) => x.fields.Status === 'Active')
      .reduce((sum, x) => sum + (appCostById.get(x.fields.AppId) ?? 0), 0);
    const infraCost = cloudSpend.reduce((sum, x) => sum + (x.fields.Amount || 0), 0);

    return {
      employeeCount: activeEmployees,
      projectCount: activeProjects,
      pendingExpenses,
      totalMonthlyCost: licenseCost + infraCost,
      licenseCost,
      infraCost,
    };
  }, [employees, projects, expenses, apps, assignments, cloudSpend]);

  const allocationByProject = useMemo(() => {
    const projectMap = new Map<number, string>();
    for (const p of projects) projectMap.set(Number(p.id), p.fields.Code);
    const counts = new Map<string, number>();
    for (const a of allocations) {
      const pCode = projectMap.get(a.fields.ProjectId) ?? 'Unknown';
      counts.set(pCode, (counts.get(pCode) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  }, [projects, allocations]);

  const recentExpenses = useMemo(
    () =>
      expenses
        .slice()
        .sort((a, b) => (b.fields.ExpenseDate ?? '').localeCompare(a.fields.ExpenseDate ?? ''))
        .slice(0, 3),
    [expenses],
  );

  // ── Pending admin actions ──────────────────────────────────────────────
  // Only computed for admins. Surfaces config gaps so the cost dashboards
  // stay accurate without admins having to dig into individual pages.
  const pendingActions = useMemo<PendingAction[]>(() => {
    if (!isAdmin) return [];
    const out: PendingAction[] = [];
    // 1. Devices needing approval
    if (listIds?.OC_Devices) {
      const pending = devices.filter((d) => d.fields.Status === 'Pending').length;
      if (pending > 0) {
        out.push({
          key: 'devices-pending',
          severity: 'high',
          icon: <Phone24Regular style={{ fontSize: 18 }} />,
          label: `${pending} device${pending > 1 ? 's' : ''} awaiting approval`,
          detail: 'Review installs in Settings → Devices',
          route: '/devices',
        });
      }
    } else {
      out.push({
        key: 'devices-not-setup',
        severity: 'medium',
        icon: <Warning24Regular style={{ fontSize: 18 }} />,
        label: 'Device enrollment not set up',
        detail: 'One-click setup creates the SharePoint list',
        route: '/devices',
      });
    }
    // 2. Cost allocation gaps
    if (listIds?.OC_CostAllocations) {
      // Compute per-subscription allocation totals
      const allocBySub = new Map<string, number>();
      for (const a of costAllocations) {
        if (a.fields.ResourceType !== 'Subscription') continue;
        allocBySub.set(
          a.fields.ResourceId,
          (allocBySub.get(a.fields.ResourceId) ?? 0) + (a.fields.AllocationPercent || 0),
        );
      }
      // Subs that have spend but no allocation, or under/over-allocated
      const subsWithSpend = new Set(cloudSpend.map((s) => s.fields.SubscriptionId).filter(Boolean));
      let unallocatedCount = 0;
      let overCount = 0;
      for (const subId of subsWithSpend) {
        const total = allocBySub.get(subId) ?? 0;
        if (total < 100) unallocatedCount += 1;
        if (total > 100) overCount += 1;
      }
      if (overCount > 0) {
        out.push({
          key: 'cost-over',
          severity: 'high',
          icon: <Warning24Regular style={{ fontSize: 18 }} />,
          label: `${overCount} subscription${overCount > 1 ? 's' : ''} over-allocated`,
          detail: 'Allocations sum to >100% — fix before reports drift',
          route: '/cost-allocation',
        });
      }
      if (unallocatedCount > 0) {
        out.push({
          key: 'cost-under',
          severity: 'medium',
          icon: <Money24Regular style={{ fontSize: 18 }} />,
          label: `${unallocatedCount} subscription${unallocatedCount > 1 ? 's' : ''} not fully allocated`,
          detail: 'Spend rolls into "Unallocated" until you assign projects',
          route: '/cost-allocation',
        });
      }
    } else {
      out.push({
        key: 'cost-not-setup',
        severity: 'medium',
        icon: <Money24Regular style={{ fontSize: 18 }} />,
        label: 'Cost allocation not set up',
        detail: 'Map subscriptions to projects so Cost Center attributes spend',
        route: '/cost-allocation',
      });
    }
    return out;
  }, [isAdmin, listIds, devices, costAllocations, cloudSpend]);

  if (empLoading && !listIds) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spinner size="large" label="Loading Prism..." />
      </div>
    );
  }

  const canShowCost = currentUser ? canViewCosts(currentUser.role) : false;

  return (
    <div className={styles.page}>
      {/* Greeting + Large title (desktop only) */}
      <div>
        <Body2 className={styles.greeting}>
          {getGreeting()}
          {currentUser ? `, ${currentUser.displayName.split(' ')[0]}` : ''}
        </Body2>
        <div className={styles.title}>Dashboard</div>
      </div>

      {/* Primary CTA */}
      <Button
        appearance="primary"
        icon={<Add24Regular />}
        size="large"
        className={styles.ctaButton}
        onClick={() => navigate('/expenses')}
      >
        Submit an expense
      </Button>

      {/* Hero metrics */}
      <div className={styles.heroGrid}>
        <div className={styles.heroCard} onClick={() => navigate('/members')}>
          <div className={styles.heroLabel}>
            <People24Regular style={{ fontSize: 14 }} />
            People
          </div>
          <div className={styles.heroValue}>{stats.employeeCount}</div>
        </div>
        <div className={styles.heroCard} onClick={() => navigate('/projects')}>
          <div className={styles.heroLabel}>
            <Briefcase24Regular style={{ fontSize: 14 }} />
            Projects
          </div>
          <div className={styles.heroValue}>{stats.projectCount}</div>
        </div>
        {canShowCost && (
          <>
            <div className={styles.heroCard} onClick={() => navigate('/costs')}>
              <div className={styles.heroLabel}>
                <Money24Regular style={{ fontSize: 14 }} />
                Monthly
              </div>
              <div className={styles.heroValue}>
                {formatCurrency(stats.totalMonthlyCost)}
              </div>
            </div>
            <div className={styles.heroCard} onClick={() => navigate('/expenses')}>
              <div className={styles.heroLabel}>
                <Receipt24Regular style={{ fontSize: 14 }} />
                Pending
              </div>
              <div className={styles.heroValue}>{stats.pendingExpenses}</div>
            </div>
          </>
        )}
      </div>

      {/* Pending admin actions — only renders if admin + something needs doing */}
      {pendingActions.length > 0 && (
        <div className={styles.alertCard}>
          <div className={styles.alertHeader}>
            <Warning24Regular style={{ fontSize: 16 }} />
            Action needed ({pendingActions.length})
          </div>
          {pendingActions.map((a) => (
            <div
              key={a.key}
              className={styles.alertRow}
              onClick={() => navigate(a.route)}
              role="button"
              tabIndex={0}
            >
              <div
                className={`${styles.alertIcon} ${a.severity === 'high' ? styles.alertIconHigh : styles.alertIconMed}`}
              >
                {a.icon}
              </div>
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: tokens.colorNeutralForeground1,
                    lineHeight: 1.3,
                  }}
                >
                  {a.label}
                </span>
                <span
                  style={{
                    fontSize: 12,
                    color: tokens.colorNeutralForeground3,
                    lineHeight: 1.35,
                  }}
                >
                  {a.detail}
                </span>
              </div>
              <ChevronRight24Regular
                style={{ fontSize: 16, color: tokens.colorNeutralForeground3, flexShrink: 0 }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Allocation donut — single chart, clean */}
      {allocationByProject.length > 0 && (
        <div className={styles.section}>
          <Title3 className={styles.sectionTitle}>Top projects by people</Title3>
          <div className={`${styles.card} ${styles.cardPadded}`}>
            <div className={styles.chartContainer}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={allocationByProject}
                    cx="50%"
                    cy="50%"
                    innerRadius="55%"
                    outerRadius="85%"
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, value }) => `${name} · ${value}`}
                    labelLine={false}
                  >
                    {allocationByProject.map((_, idx) => (
                      <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Recent expenses — compact list */}
      {recentExpenses.length > 0 && (
        <div className={styles.section}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0 4px',
            }}
          >
            <Title3 className={styles.sectionTitle}>Recent expenses</Title3>
            <Button
              appearance="subtle"
              size="small"
              onClick={() => navigate('/expenses')}
            >
              See all
            </Button>
          </div>
          <div className={styles.card}>
            {recentExpenses.map((exp) => (
              <div
                key={exp.id}
                className={styles.listRow}
                onClick={() => navigate('/expenses')}
              >
                <div className={styles.listIcon}>
                  <Receipt24Regular />
                </div>
                <div className={styles.listBody}>
                  <Body2 style={{ fontWeight: 500 }}>
                    {exp.fields.Description || exp.fields.Category}
                  </Body2>
                  <Caption1>
                    {exp.fields.Category} ·{' '}
                    {exp.fields.EmployeeEmail.split('@')[0]}
                  </Caption1>
                </div>
                <div style={{ textAlign: 'right' as const }}>
                  <Body1 style={{ fontWeight: 600 }}>
                    {formatCurrency(exp.fields.Amount)}
                  </Body1>
                  <Caption1>{exp.fields.Status}</Caption1>
                </div>
                <ChevronRight24Regular style={{ color: tokens.colorNeutralForeground3, flexShrink: 0 }} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
